<template>
  <vf-input
    ref="phoneRef"
    v-model="phone"
    data-test-id="input-phone"
    :disabled
    inputmode="tel"
    :invalid
    :label="label || $t.mobileNumber"
    :required
    type="tel"
    :variant
    @blur="$emit('change', $event)"
    @change="$emit('change', $event)"
  >
    <template #before>
      <slot name="before">
        <base-popover
          v-if="showCountryCode"
          v-slot="{ close, open, side }"
          :arrow-shift="-6"
          class="relative my-px ml-px flex shrink-0 center self-stretch"
          flip
          :offset="{ mainAxis: 10, alignmentAxis: -20 }"
          placement="top"
        >
          <span class="relative flex items-center self-stretch gap-2 pl-4 pr-2 rounded-l-sm bg-grey-90">
            <vf-location-flag
              class="pointer-events-none shrink-0"
              :country-code="selectedCountryCode"
            />
            <vf-icon class="pointer-events-none shrink-0" dir="down" name="chevron" />
            <base-select
              v-model="localCode"
              :aria-label="$t.mobileCountryCode"
              class="absolute-0 form-input"
              data-test-id="form-input-phone-select"
              :disabled="disableCode || disabled"
              :label-formatter="() => ' '"
              :options
              @blur="close"
              @focus="open"
              @keydown.esc="close"
              @mouseenter="open"
              @mouseleave="close"
            />
          </span>
          <base-popover-content
            :id
            class="z-overlay ws-nowrap b b-grey-70 bg-white px-4 py-6 text-sm brand-base shadow-xs transition"
            data-test-id="vf-tooltip-content"
            enter-active-class="transform ease-out"
            enter-from-class="op-0 scale-95"
            enter-to-class="translate-y-0 scale-100"
            leave-active-class="transform ease-in"
            leave-from-class="translate-y-0 scale-100"
            leave-to-class="op-0 scale-95"
            role="tooltip"
            style="max-width: 15rem"
          >
            {{ $t.mobileCountryCode }}
            <base-popover-arrow
              class="absolute transform b-grey-70 bg-white"
              :class="side === 'top' ? 'b-b b-r' : 'b-t b-l'"
              style="width: 0.625rem; height: 0.625rem; rotate: 45deg"
            />
          </base-popover-content>
        </base-popover>
      </slot>
    </template>
    <template v-if="$slots.after" #after>
      <slot name="after" />
    </template>
  </vf-input>
</template>

<script lang="ts" setup>
import type { CountryCode } from '#types/locale'
import phoneCodes from 'assets/phoneCodes.json'

const { addressCountryCode, showCountryCode = true, variant } = defineProps<{
  addressCountryCode?: string
  disableCode?: boolean
  disabled?: boolean
  invalid?: boolean
  label?: string
  required?: boolean
  showCountryCode?: boolean
  variant?: 'inverse'
}>()

defineEmits<{ change: [value: string] }>()

defineSlots<{
  default?: () => void
  before?: () => void
  after?: () => void
}>()

const model = defineModel<string>({ required: true })
const code = defineModel<string>('code')

const id = useId()
const phoneRef = useTemplateRef('phoneRef')

const countryCode = computed<Uppercase<CountryCode>>(() => code.value
  ? getCountryCodeByPhoneCode(code.value, addressCountryCode || useCountryCode()) as Uppercase<CountryCode>
  : useCountryCode()
)

const codes = phoneCodes.map(({ name, dialCode }) => ({
  label: `${name} (+${dialCode})`,
  value: dialCode
}))

const codeCounts = phoneCodes.reduce((acc, { dialCode }) => {
  if (!acc[dialCode]) acc[dialCode] = 0
  acc[dialCode]++
  return acc
}, {})

const codesWithCountryCode = phoneCodes.map(({ name, dialCode, code }) => ({
  label: `${name} (+${dialCode})`,
  value: codeCounts[dialCode] > 1 ? `${dialCode}-${code}` : dialCode,
  dialCode
}))

const options = showCountryCode ? codesWithCountryCode : codes

const localCode = computed({
  get() {
    return showCountryCode && code.value && codeCounts[code.value] > 1
      ? `${code.value}-${countryCode.value}`
      : code.value
  },
  set(event) {
    code.value = (event?.split('-') || [])[0]
  }
})

const selectedCountryCode = computed<Uppercase<CountryCode>>(() => {
  const value = localCode.value
  if (!value) return useCountryCode()

  const [, isoCode] = String(value).split('-')
  if (isoCode) return isoCode.toUpperCase() as Uppercase<CountryCode>

  return countryCode.value
})

const { value: phone, clearValue, el } = useMask((value) => {
  const mask = getPhoneMaskByPhoneCode(localCode.value ?? '1', countryCode.value)
  if (!mask) return ''
  if (isString(mask)) return mask
  const phoneLength = Array.from(value.matchAll(/\d/g)).length
  // In case our phoneCode for the locale is an array of masks with the different lengths, i.e. LU ['XXX - XXX', 'XXX - XXX - XXX'],
  // we want to default to the one with the longest length rather than the first item in the array.
  const mostXsItem = mask.reduce((maxItem, currentItem) => {
    const numXs = (currentItem.match(/X/g) || []).length
    return numXs > (maxItem.match(/X/g) || []).length ? currentItem : maxItem
  }, '')

  return mask.find((subMask) => phoneLength === subMask.match(/X/g)?.length) || mostXsItem
}, {
  regex: /\d/,
  onUpdate: (v) => model.value = v
})

function resolveNativeInputEl(): HTMLInputElement | undefined {
  const comp = phoneRef.value as any
  const inputRef = comp?.inputRef
  if (!inputRef) return undefined

  const direct
    = (inputRef.el as HTMLInputElement | undefined)
      ?? (inputRef.$el as HTMLInputElement | undefined)

  if (direct && direct.tagName?.toLowerCase() === 'input') {
    return direct
  }

  const wrapper
    = (inputRef.el as HTMLElement | undefined)
      ?? (inputRef.$el as HTMLElement | undefined)
  if (!wrapper) return undefined

  return wrapper.querySelector('input') ?? undefined
}

onMounted(() => {
  const inputEl = resolveNativeInputEl()
  if (inputEl) {
    el.value = inputEl as unknown as HTMLInputElement
  }
})

watch(
  [model, el],
  (_value) => {
    if (el.value)
      clearValue.value = model.value
  },
  { immediate: true }
)
</script>
